import { groupBy, sortBy } from "lodash";
import startCase from "lodash/startCase";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { isExportRestricted } from "@app/authorization";
import {
  ExportProviderAvailabilityType,
  ExportType,
  ReportFormatType,
  ReportSettingsType,
} from "@app/exports/enumerations";
import ClosestProviderExportsPanelBody from "@app/exports/export-panel-body/ClosestProviderExportsPanelBody";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { SpecialtySummaryByZipCodePanelBody } from "@app/exports/export-panel-body/SpecialtySummaryByZipCodePanelBody";
import { ReportSettingsModel } from "@app/exports/exportModels";
import ExportService from "@app/exports/exportService";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { Module } from "@app/exports/literals";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { NetworkAssessmentAnalysisType, ProjectPerspective } from "@app/projects/enumerators";
import NetNewProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { RegionGranularity, RegionSet } from "@app/region-sets";
import RegionSetService from "@app/region-sets/regionSetService";
import { SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { ActionableProvidersPanelBody } from "@app/exports/export-panel-body/ActionableProvidersPanelBody";
import AllInspectionsPanelBody from "@app/exports/export-panel-body/AllInspectionsPanelBody";
import AllSpecialtiesPanelBody from "@app/exports/export-panel-body/AllSpecialtiesPanelBody";
import CountyAggregatePanelBody from "@app/exports/export-panel-body/CountyAggregatePanelBody";
import NewProviderReportPanelBody from "@app/exports/export-panel-body/NewProviderReportPanelBody";
import NewProviderReportNoPublishedRunNotice from "@app/exports/export-panel-body/NewProviderReportNoPublishedRunNotice";
import NewProviderReportPastRunNotice from "@app/exports/export-panel-body/NewProviderReportPastRunNotice";
import NewProviderReportUnpublishedRunNotice from "@app/exports/export-panel-body/NewProviderReportUnpublishedRunNotice";
import RegionAggregatePanelBody from "@app/exports/export-panel-body/RegionAggregatePanelBody";
import ServicingProvidersPanelBody from "@app/exports/export-panel-body/ServicingProvidersPanelBody";
import SpecialtyAggregatePanelBody from "@app/exports/export-panel-body/SpecialtyAggregatePanelBody";
import SpecialtyAggregateVolumePanelBody from "@app/exports/export-panel-body/SpecialtyAggregateVolumePanelBody";
import SpecialtySummaryByRegionPanelBody from "@app/exports/export-panel-body/SpecialtySummaryByRegionPanelBody";
import StateAggregatePanelBody from "@app/exports/export-panel-body/StateAggregatePanelBody";
import StatusReportPanelBody from "@app/exports/export-panel-body/StatusReportPanelBody";
import SummariesDetailPanelBody from "@app/exports/export-panel-body/SummariesDetailPanelBody";
import { ZipCodeServicingProvidersPanel } from "@app/exports/export-panel-body/ZipCodeServicingProvidersPanelBody";
import ZipSummariesExportsPanelBody from "@app/exports/export-panel-body/ZipSummariesExportsPanelBody";
import { isNewProviderReportEligible } from "@app/exports/newProviderReportEligibility";

type ExportDataProps = RouteComponentProps<{
  isLatestPublished: string;
  projectId: string;
  runConfigurationId: string;
  module: Module;
}> &
  UserContextProps &
  FeatureFlagContextProps;

interface ReportSettingsState {
  actionableProvidersReportSettings: ReportSettingsModel[];
  allInspectionsReportSettings: ReportSettingsModel[];
  countyAggregateReportSettings: ReportSettingsModel[];
  stateAggregateReportSettings: ReportSettingsModel[];
  specialtyAggregateVolumeReportSettings: ReportSettingsModel[];
  servicingProvidersReportSettings: ReportSettingsModel[];
  specialtySummaryByRegionReportSettings: ReportSettingsModel[];
  summariesDetailReportSettings: ReportSettingsModel[];
  statusReportSettings: ReportSettingsModel[];
  zipCodeServicingProviderSettings: ReportSettingsModel[];
  zipSummaryReportSettings: ReportSettingsModel[];
}

interface ExportDataState extends ExportDataRouteState, ReportSettingsState {
  regionSets: RegionSet[];
}

class ExportData extends React.Component<ExportDataProps, ExportDataState> {
  private readonly exportService = new ExportService();
  private readonly regionSetService = new RegionSetService();
  private readonly netNewServicingProviderService = new NetNewProviderService();
  private readonly maxExcelRecords = 1000000; // MS Excel sheet row limit is 1,048,576
  private readonly nonRecordRows = 50; // buffer for header, footer
  private readonly excelRecordsLimit = this.maxExcelRecords - this.nonRecordRows;
  private readonly mapMarkerIcon = (
    <QesIcon iconName={QesIcons.MarkerPinV1} description="Map" className="align-text-top me-1" />
  );
  private readonly userIcon = <QesIcon iconName={QesIcons.UserV1} description="User" className="align-text-top me-1" />;

  constructor(props: ExportDataProps) {
    super(props);

    const routeState = this.extractRouteState();
    this.state = {
      ...routeState,
      actionableProvidersReportSettings: [],
      allInspectionsReportSettings: [],
      countyAggregateReportSettings: [],
      regionSets: null,
      servicingProvidersReportSettings: [],
      showNetNewServicingProviders: false,
      specialtyAggregateVolumeReportSettings: [],
      specialtySummaryByRegionReportSettings: [],
      stateAggregateReportSettings: [],
      statusReportSettings: [],
      summariesDetailReportSettings: [],
      zipCodeServicingProviderSettings: [],
      zipSummaryReportSettings: [],
    };
  }

  public async componentDidMount() {
    const regionSets: RegionSet[] = await this.regionSetService.getByIds(this.state.regionSetIds);

    const allUserReportSettings: ReportSettingsModel[] = await this.exportService.getAllUserReportSettings();
    const reportSettingsByType = groupBy(allUserReportSettings, "type");
    let actionableProvidersReportSettings: ReportSettingsModel[];
    let allInspectionsReportSettings: ReportSettingsModel[];
    let countyAggregateReportSettings: ReportSettingsModel[];
    let stateAggregateReportSettings: ReportSettingsModel[];
    let servicingProvidersReportSettings: ReportSettingsModel[];
    let specialtyAggregateVolumeReportSettings: ReportSettingsModel[];
    let specialtySummaryByRegionReportSettings: ReportSettingsModel[];
    let statusReportSettings: ReportSettingsModel[];
    let summariesDetailReportSettings: ReportSettingsModel[];
    let zipCodeServicingProviderSettings: ReportSettingsModel[];
    let zipSummaryReportSettings: ReportSettingsModel[];

    if (this.props.user.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
      this.netNewServicingProviderService
        .projectHasNetNewServicingProviders(this.projectId)
        .then((hasNetNew) => this.setState({ showNetNewServicingProviders: hasNetNew }));
    }

    switch (this.module) {
      case "accuracy":
        actionableProvidersReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AccuracyActionableProviders],
          "name",
        );
        allInspectionsReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AccuracyAllInspections],
          "name",
        );
        countyAggregateReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AdequacyCountyAggregate],
          "name",
        );
        specialtyAggregateVolumeReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AdequacySpecialtyAggregateVolume],
          "name",
        );
        specialtySummaryByRegionReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AccuracySpecialtySummaryByRegion],
          "name",
        );
        stateAggregateReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AdequacyStateAggregate],
          "name",
        );
        statusReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AccuracyStatusReport],
          "name",
        );
        summariesDetailReportSettings = this.sortedReportSettings(
          reportSettingsByType[ReportSettingsType.AccuracySummariesDetail],
          "name",
        );
      default:
        break;
    }

    this.setState({
      actionableProvidersReportSettings,
      allInspectionsReportSettings,
      countyAggregateReportSettings,
      regionSets,
      servicingProvidersReportSettings,
      specialtyAggregateVolumeReportSettings,
      specialtySummaryByRegionReportSettings,
      stateAggregateReportSettings,
      statusReportSettings,
      summariesDetailReportSettings,
      zipCodeServicingProviderSettings,
      zipSummaryReportSettings,
    });
  }

  public render() {
    if (this.props.enabledFeatureFlags.size === 0) {
      return <LoadingSpinner />;
    }

    let firstRendered = !!this.state.activeExportType;
    const filtersToRender: React.ReactElement[] = [];

    // build up a list of accordion panels and make sure the first one (which might not always be the same) defaults to expanded
    // All Specialties (intentionally placed first, ahead of Region Summary and Specialty Summary)
    if (this.state.availableExportTypes.includes(ExportType.AllSpecialties)) {
      filtersToRender.push(this.renderAllSpecialties(firstRendered));
      firstRendered = true;
    }
    // Specialty Summary By Region (intentionally placed just after All Specialties)
    if (this.state.availableExportTypes.includes(ExportType.SpecialtySummaryByRegion)) {
      filtersToRender.push(this.renderSpecialtySummaryByRegion(firstRendered));
      firstRendered = true;
    }
    // CountyAggregate By Region
    if (this.state.availableExportTypes.includes(ExportType.CountyAggregate)) {
      filtersToRender.push(this.renderCountyAggregateByRegion(firstRendered));
      firstRendered = true;
    }
    // StateAggregate By Region
    if (this.state.availableExportTypes.includes(ExportType.StateAggregate)) {
      filtersToRender.push(this.renderStateAggregateByRegion(firstRendered));
      firstRendered = true;
    }
    // SpecialtyAggregateVolume By Region
    if (this.state.availableExportTypes.includes(ExportType.SpecialtyAggregateVolume)) {
      filtersToRender.push(this.renderSpecialtyAggregagteVolumeByRegion(firstRendered));
      firstRendered = true;
    }
    // Region Summary
    if (this.state.availableExportTypes.includes(ExportType.RegionAggregate)) {
      filtersToRender.push(this.renderRegionAggregate(firstRendered));
      firstRendered = true;
    }
    // Specialty Summary
    if (this.state.availableExportTypes.includes(ExportType.SpecialtyAggregate)) {
      filtersToRender.push(this.renderSpecialtyAggregate(firstRendered));
      firstRendered = true;
    }
    // All inspections
    if (this.state.availableExportTypes.includes(ExportType.AllInspections)) {
      filtersToRender.push(this.renderInspectionSummary(firstRendered));
      firstRendered = true;
    }
    // Actionable Providers
    if (this.state.availableExportTypes.includes(ExportType.ActionableProviders)) {
      filtersToRender.push(this.renderActionableProviders(firstRendered));
      firstRendered = true;
    }
    // Servicing Providers
    if (this.state.availableExportTypes.includes(ExportType.ServicingProviders)) {
      filtersToRender.push(this.renderServicingProviders(firstRendered));
      firstRendered = true;
    }
    // Status Report
    if (this.state.availableExportTypes.includes(ExportType.StatusReport)) {
      filtersToRender.push(this.renderStatusReport(firstRendered));
      firstRendered = true;
    }
    // New Provider Report
    if (
      this.state.availableExportTypes.includes(ExportType.NewProviderReport) &&
      isNewProviderReportEligible(this.props.enabledFeatureFlags, this.props.user.roles)
    ) {
      filtersToRender.push(this.renderNewProviderReport(firstRendered));
      firstRendered = true;
    }
    // Accuracy Detail
    if (this.state.availableExportTypes.includes(ExportType.AccuracyDetail)) {
      filtersToRender.push(this.renderAccuracyDetail(firstRendered));
      firstRendered = true;
    }
    // Zip Summaries
    if (this.state.availableExportTypes.includes(ExportType.ZipSummary)) {
      filtersToRender.push(this.renderZipSummaries(firstRendered));
      firstRendered = true;
    }
    // Closest Providers
    if (this.state.availableExportTypes.includes(ExportType.ClosestProvider)) {
      filtersToRender.push(this.renderClosestProviders(firstRendered));
      firstRendered = true;
    }
    // Zip Code Analysis Table
    if (this.state.availableExportTypes.includes(ExportType.ZipAnalysisResultsReport)) {
      filtersToRender.push(this.renderSpecialtySummaryByZipCode(firstRendered));
      firstRendered = true;
    }
    if (this.state.availableExportTypes.includes(ExportType.ZipClassAggregateResultsReport)) {
      filtersToRender.push(this.renderSpecialtySummaryByZipClass(firstRendered));
      firstRendered = true;
    }
    // Zip Code Analysis Servicing Providers
    if (this.state.availableExportTypes.includes(ExportType.ZipAnalysisAdequacyServicingProvider)) {
      filtersToRender.push(this.renderZipCodeServicingProviders(firstRendered));
      firstRendered = true;
    }

    return this.renderInternal(filtersToRender);
  }

  private renderInternal(filtersToRender: React.ReactElement[]) {
    const module = this.module.charAt(0).toUpperCase() + this.module.slice(1);
    const filterBreadcrumbs = this.state.filters.map((f) => f.displayText);
    let breadcrumbs: (string | Breadcrumb)[] = breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment];
    breadcrumbs =
      this.state.projectResourceId && this.state.projectResourceName
        ? [
            ...breadcrumbs,
            {
              link: `/projects/${this.state.projectResourceId}`,
              text: this.state.projectResourceName,
            },
            module,
            ...filterBreadcrumbs,
            "Create Report",
          ]
        : [...breadcrumbs, "Loading..."];
    const inspectorCount = this.state.selectedInspectors.flatMap((si) => si?.inspectors.map((i) => i)).length;

    return (
      <Page documentTitle="Create Downloadable Report">
        <Breadcrumbs items={breadcrumbs} />
        <PageInner>
          <PageHeader iconName={QesIcons.DownloadCloudV1} title="Create Downloadable Report" disableHR={false}>
            <PageHeaderRight>
              <div className="d-flex">
                <QESButton
                  qesTestId="export-data_return-to-project"
                  qesType="secondary"
                  onClick={() => this.props.history.goBack()}
                  className="me-2"
                >
                  Return to Project
                </QESButton>
                <Link to="/reports" className="qes-button qes-button-primary" type="button">
                  Reports
                </Link>
              </div>
            </PageHeaderRight>
          </PageHeader>

          <PageSection>
            <h3 className="my-4">{module}&nbsp;Reports</h3>
            {inspectorCount > 0 && (
              <Accordion
                testId="export-data__inspectors-accordion"
                title={`Inspectors: ${this.state.allInspectorsSelected ? "All" : inspectorCount} selected`}
              >
                {this.state.selectedInspectors.map((group) => {
                  return (
                    <div key={group.name} className="d-flex dashboard-filters-overview__list">
                      <span className="dashboard-filters-overview__label">{group.name}</span>
                      <span>
                        {group.inspectors.map((inspector) => (
                          <span key={inspector} className="dashboard-filters-overview__filter">
                            {inspector}
                          </span>
                        ))}
                      </span>
                    </div>
                  );
                })}
              </Accordion>
            )}
            <AccordionContainer>{filtersToRender}</AccordionContainer>
          </PageSection>
        </PageInner>
      </Page>
    );
  }

  private renderRegionAggregate(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="ra">
        <Accordion
          onPanelWillOpen={this.handleRegionSetPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.RegionAggregate}
          title={
            <>
              {this.mapMarkerIcon}
              {`${startCase(this.singularDisplay())} Summary`}
            </>
          }
          testId="export-data__region-aggregate-accordion"
        >
          <RegionAggregatePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderSpecialtyAggregate(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="sa">
        <Accordion
          onPanelWillOpen={this.handleSpecialtyAggregatePaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.SpecialtyAggregate}
          title="Specialty Summary"
          testId="export-data__specialty-summary-accordion"
        >
          <SpecialtyAggregatePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderAllSpecialties(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="as">
        <Accordion
          onPanelWillOpen={this.handleAllSpecialtiesPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.AllSpecialties}
          title="All Specialties"
          testId="export-data__all-specialties-accordion"
        >
          <AllSpecialtiesPanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderSpecialtySummaryByRegion(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="ssbr">
        <Accordion
          onPanelWillOpen={this.handleSpecialtySummaryByCountyPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.SpecialtySummaryByRegion}
          title={
            <>
              {this.mapMarkerIcon}
              {
                `Specialty Summary By County` /* ${startCase(this.singularDisplay())} intentionally hard-coded to county until we can do on-the-fly aggregation - 7/31/2019 */
              }
            </>
          }
          testId="export-data__specialty-summary-by-county-accordion"
        >
          <SpecialtySummaryByRegionPanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={`counties` /*this.pluralDisplay()*/}
            projectId={this.projectId}
            runConfigurationId={this.runConfigurationId}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            enableMembershipTypeOptions={this.state.enableMembershipTypeOptions}
            singularDisplay={`county` /*this.singularDisplay()*/}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderCountyAggregateByRegion(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="ca">
        <Accordion
          onPanelWillOpen={this.handleCountyAggregatePanelClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.CountyAggregate}
          title={
            <>
              {this.mapMarkerIcon}
              {`County Aggregate Report`}
            </>
          }
          testId="export-data__county-aggregate-accordion"
        >
          <CountyAggregatePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={`counties` /*this.pluralDisplay()*/}
            projectId={this.projectId}
            reportSettingsList={this.state.countyAggregateReportSettings}
            runConfigurationId={this.runConfigurationId}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            singularDisplay={`county` /*this.singularDisplay()*/}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderStateAggregateByRegion(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="stateaggregate">
        <Accordion
          onPanelWillOpen={this.handleStateAggregatePanelClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.StateAggregate}
          title={<>{this.mapMarkerIcon}State Aggregate Report</>}
          testId="export-data__state-aggregate-accordion"
        >
          <StateAggregatePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={`states` /*this.pluralDisplay()*/}
            projectId={this.projectId}
            reportSettingsList={this.state.stateAggregateReportSettings}
            runConfigurationId={this.runConfigurationId}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            singularDisplay={`state` /*this.singularDisplay()*/}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderSpecialtyAggregagteVolumeByRegion(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="sav">
        <Accordion
          onPanelWillOpen={this.handleSpecialtyAggregateVolumePanelClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.SpecialtyAggregateVolume}
          title={<>{this.mapMarkerIcon}Specialty Aggregate Report</>}
          testId="export-data__specialty-aggregate-accordion"
        >
          <SpecialtyAggregateVolumePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={`specialties` /*this.pluralDisplay()*/}
            projectId={this.projectId}
            reportSettingsList={this.state.specialtyAggregateVolumeReportSettings}
            runConfigurationId={this.runConfigurationId}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            singularDisplay={`specialty` /*this.singularDisplay()*/}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderServicingProviders(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="sp">
        <Accordion
          onPanelWillOpen={this.handleServicingProvidersPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.ServicingProviders}
          title={<>{this.userIcon}Providers</>}
          testId="export-data__providers-accordion"
        >
          <ServicingProvidersPanelBody
            activeNetworkType={this.state.activeNetworkType}
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            excelRecordsLimit={this.excelRecordsLimit}
            exportNetworkProviderCount={this.state.exportNetworkProviderCount}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            providerAvailabilityType={this.state.providerAvailabilityType}
            reportSettingsList={this.state.servicingProvidersReportSettings}
            runConfigurationId={this.runConfigurationId}
            showCountyFirst={
              this.state.perspective === ProjectPerspective.ByCounty ||
              this.state.perspective === ProjectPerspective.ByState
            }
            showNextClosestProvider={this.state.nextClosestProviders}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            showNetNewServicingProviders={this.state.showNetNewServicingProviders}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderAccuracyDetail(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="ad">
        <Accordion
          onPanelWillOpen={this.handleAccuracyDetailsPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.AccuracyDetail}
          title={
            <>
              {this.userIcon}
              {this.reportNameBase()}
            </>
          }
          testId="export-data__accuracy-detail-accordion"
        >
          <SummariesDetailPanelBody
            defaultReportName={helpers.reportName(this.reportNameBase())}
            displayKey={this.state.displayKey}
            excelRecordsLimit={this.excelRecordsLimit}
            exportSummaryProviderCount={this.state.exportSummaryProviderCount}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            reportSettingsList={this.state.summariesDetailReportSettings}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderInspectionSummary(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="ai">
        <Accordion
          onPanelWillOpen={this.handleAccuracyDetailsPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.AllInspections}
          title={<>{this.userIcon}Inspection Summary</>}
          testId="export-data__inspection-summary-accordion"
        >
          <AllInspectionsPanelBody
            displayKey={this.state.displayKey}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            reportSettingsList={this.state.allInspectionsReportSettings}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderZipSummaries(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="zip">
        <Accordion
          onPanelWillOpen={this.handleZipSummariesPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.ZipSummary}
          title="Zip Summaries"
          testId="export-data__zip-summaries-accordion"
        >
          <ZipSummariesExportsPanelBody
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            reportSettingsList={this.state.zipSummaryReportSettings}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderClosestProviders(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="closestProviders">
        <Accordion
          onPanelWillOpen={this.handleClosestProviderPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.ClosestProvider}
          title={<>{this.userIcon}Closest Providers to Members</>}
          testId="export-data__closest-providers-accordion"
        >
          <ClosestProviderExportsPanelBody
            history={this.props.history}
            location={this.props.location}
            match={this.props.match}
            projectId={this.projectId}
            projectUrl={this.state.projectURL}
            regionSetIds={this.state.regionSetIds}
            runConfigurationId={this.runConfigurationId}
            networkAssessmentAnalysisType={this.state.initialNetworkAssessmentAnalysisType}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderSpecialtySummaryByZipCode(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="zipCode">
        <Accordion
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.ZipAnalysisResultsReport}
          title={<>{this.mapMarkerIcon}Specialty Summary by Zip Code</>}
          testId="export-data__specialty-summary-by-zip-code-accordion"
        >
          <SpecialtySummaryByZipCodePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay=""
            projectId={this.projectId}
            reportSettingsList={this.state.specialtySummaryByRegionReportSettings}
            runConfigurationId={this.runConfigurationId}
            searchParameters={this.state.zipProjectSearchParameters}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            singularDisplay={"zip"}
            sortedReportSettings={this.sortedReportSettings}
            reportType="zipCode"
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderSpecialtySummaryByZipClass(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="zipClass">
        <Accordion
          defaultOpen={
            !firstAlreadyRendered || this.state.activeExportType === ExportType.ZipClassAggregateResultsReport
          }
          title={<>{this.mapMarkerIcon}Specialty Summary by Zip Code Classification</>}
          testId="export-data__specialty-summary-by-zip-class-accordion"
        >
          <SpecialtySummaryByZipCodePanelBody
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay=""
            projectId={this.projectId}
            reportSettingsList={this.state.specialtySummaryByRegionReportSettings}
            runConfigurationId={this.runConfigurationId}
            searchParameters={this.state.zipProjectSearchParameters}
            showExpandableSpecialtiesOption={this.state.showExpandableSpecialties}
            singularDisplay={"zip"}
            sortedReportSettings={this.sortedReportSettings}
            reportType="zipClass"
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderZipCodeServicingProviders(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="zipServicingProviders">
        <Accordion
          onPanelWillOpen={this.handleZipCodeServicingProvidersPaneClick}
          defaultOpen={
            !firstAlreadyRendered || this.state.activeExportType === ExportType.ZipAnalysisAdequacyServicingProvider
          }
          title={<>{this.userIcon}Providers by Zip Code</>}
          testId="export-data__zip-code-providers-accordion"
        >
          <ZipCodeServicingProvidersPanel
            activeNetworkType={this.state.activeNetworkType}
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            exportNetworkProviderCount={this.state.exportNetworkProviderCount}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            providerAvailabilityType={this.state.providerAvailabilityType}
            reportSettingsList={this.state.servicingProvidersReportSettings}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            showNetNewServicingProviders={this.state.showNetNewServicingProviders}
            regionSetIds={this.state.regionSetIds}
            specialtyGroupCodes={this.state.specialtyGroupCodes}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderActionableProviders(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="apar">
        <Accordion
          onPanelWillOpen={this.handleActionableProvidersPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.ActionableProviders}
          title={<>{this.userIcon}Actionable Providers</>}
          testId="export-data__actionable-providers-accordion"
        >
          <ActionableProvidersPanelBody
            activeNetworkType={this.state.activeNetworkType}
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            exportNetworkProviderCount={this.state.exportNetworkProviderCount}
            maxExcelRecords={this.maxExcelRecords}
            nonRecordRows={this.nonRecordRows}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            providerAvailabilityType={this.state.providerAvailabilityType}
            reportSettingsList={this.state.actionableProvidersReportSettings}
            runConfigurationId={this.runConfigurationId}
            showCountyFirst={
              this.state.perspective === ProjectPerspective.ByCounty ||
              this.state.perspective === ProjectPerspective.ByState
            }
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            inspectionBand={this.state.inspectionBand}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderStatusReport(firstAlreadyRendered: boolean) {
    return (
      <React.Fragment key="sr">
        <Accordion
          onPanelWillOpen={this.handleStatusReportPaneClick}
          defaultOpen={!firstAlreadyRendered || this.state.activeExportType === ExportType.StatusReport}
          title="Provider Status Report"
          testId="export-data__status-report-accordion"
        >
          <StatusReportPanelBody
            activeNetworkType={this.state.activeNetworkType}
            comparedRunConfigurationId={this.state.comparedRunConfigurationId}
            pluralDisplay={this.pluralDisplay()}
            projectId={this.projectId}
            providerAvailabilityType={this.state.providerAvailabilityType}
            reportSettingsList={this.state.statusReportSettings}
            runConfigurationId={this.runConfigurationId}
            singularDisplay={this.singularDisplay()}
            sortedReportSettings={this.sortedReportSettings}
            inspectionBand={this.state.inspectionBand}
            {...this.state}
            {...this.props}
          />
        </Accordion>
      </React.Fragment>
    );
  }

  private renderNewProviderReport(firstAlreadyRendered: boolean) {
    const hasNoPublishedRun = this.state.hasNoPublishedRun;
    const { isViewingNonCurrentRun, isViewingPastRun, isViewingUnpublishedRun } =
      helpers.resolveNewProviderReportRunState({
        hasNoPublishedRun,
        isLatestPublished: this.state.isLatestPublished,
        isSelectedRunPublished: this.state.isSelectedRunPublished,
      });
    const isRestricted = hasNoPublishedRun || isViewingNonCurrentRun;
    return (
      <React.Fragment key="npr">
        <Accordion
          className={isRestricted ? "mb-0" : undefined}
          defaultOpen={
            !isRestricted && (!firstAlreadyRendered || this.state.activeExportType === ExportType.NewProviderReport)
          }
          title="New Provider Report"
          testId="export-data__new-provider-report-accordion"
        >
          <NewProviderReportPanelBody
            isViewingPastRun={isViewingNonCurrentRun}
            isNoPublishedRun={hasNoPublishedRun}
            latestPublishedRunDate={this.state.latestPublishedRunDate}
            projectId={this.projectId}
            runConfigurationId={this.runConfigurationId}
            pluralDisplay={this.pluralDisplay()}
            singularDisplay={this.singularDisplay()}
            {...this.state}
            {...this.props}
          />
        </Accordion>
        {hasNoPublishedRun && <NewProviderReportNoPublishedRunNotice />}
        {isViewingPastRun && <NewProviderReportPastRunNotice />}
        {isViewingUnpublishedRun && <NewProviderReportUnpublishedRunNotice />}
      </React.Fragment>
    );
  }

  private get module() {
    return this.props.match.params.module;
  }

  private get projectId() {
    return Number(this.props.match.params.projectId);
  }

  private get runConfigurationId() {
    return Number(this.props.match.params.runConfigurationId);
  }

  private extractRouteState: () => ExportDataRouteState = () => {
    const routeState = (this.props.location.state || {}) as ExportDataRouteState;
    const availableExportTypes = (
      routeState.availableExportTypes || [
        ExportType.RegionAggregate,
        ExportType.SpecialtyAggregate,
        ExportType.ServicingProviders,
        ExportType.ZipAnalysisAdequacyServicingProvider,
      ]
    )
      .filter((ae) => !isExportRestricted(ae, this.module, this.props.user))
      .filter(
        (ae) =>
          routeState.showAdvancedComparison ||
          !routeState.comparedRunConfigurationId ||
          ae !== ExportType.ServicingProviders,
      );
    const networkTypes = Array.from(
      routeState.networkTypes || [
        ProviderNetworkTypeEnum.Network,
        ProviderNetworkTypeEnum.Prospect,
        ProviderNetworkTypeEnum.Market,
      ],
    );

    // Note to the developer: ALWAYS provide a default value. Assume nothing is provided.
    return {
      // TODO - Actually use activeExportType somewhere?
      accuracyFilters: routeState.accuracyFilters || null,
      activeExportType: routeState.isSummary
        ? ExportType.AccuracyDetail
        : routeState.activeExportType || availableExportTypes[0],
      activeModule: routeState.activeModule,
      activeNetworkType: routeState.activeNetworkType || networkTypes[0],
      adequacyFilters: routeState.adequacyFilters,
      allInspectorsSelected: routeState.allInspectorsSelected || false,
      availableExportTypes: routeState.isSummary ? [ExportType.AccuracyDetail] : availableExportTypes,
      comparedRunConfigurationId: routeState.comparedRunConfigurationId || null,
      countyFilters: routeState.countyFilters || null,
      defaultAppendPatternOfCareProviders: routeState.defaultAppendPatternOfCareProviders || false,
      defaultReportFormatType: routeState.defaultReportFormatType || ReportFormatType.Default,
      disableEntityTypeFilters: routeState.disableEntityTypeFilters || false,
      disableRegionFilters: routeState.disableRegionFilters || false,
      disableSpecialtyGroupFilters: routeState.disableSpecialtyGroupFilters || false,
      displayKey: routeState.displayKey || null,
      enableMembershipTypeOptions: routeState.enableMembershipTypeOptions || false,
      exportNetworkProviderCount: routeState.exportNetworkProviderCount || 0,
      exportSummaryProviderCount: routeState.exportSummaryProviderCount || 0,
      filters: routeState.filters || [],
      granularity: routeState.granularity || (routeState.isSummary ? RegionGranularity.County : undefined),
      hasMarketProviders: !!routeState.hasMarketProviders,
      hasNoPublishedRun: routeState.hasNoPublishedRun || false,
      hasProspectProviders: !!routeState.hasProspectProviders,
      initialNetworkAssessmentAnalysisType:
        routeState.initialNetworkAssessmentAnalysisType || NetworkAssessmentAnalysisType.ServiceArea,
      inspectionBand: routeState.inspectionBand || "Unknown",
      inspectionUids: routeState.inspectionUids || [],
      isLatestPublished: this.props.match.params.isLatestPublished === "true",
      isSelectedRunPublished: routeState.isSelectedRunPublished ?? true,
      isSummary: routeState.isSummary || false,
      latestPublishedRunDate: routeState.latestPublishedRunDate ?? null,
      networkType: routeState.networkType || null,
      networkTypes,
      nextClosestProviders: routeState.nextClosestProviders && !routeState.adequacyFilters.opportunity,
      perspective: routeState.perspective,
      projectResourceId: routeState.projectResourceId || null,
      projectResourceName: routeState.projectResourceName || "",
      projectURL: routeState.projectURL || "",
      providerAvailabilityType: routeState.providerAvailabilityType || ExportProviderAvailabilityType.Available,
      providerIds: routeState.providerIds || [],
      regionSetIds: routeState.regionSetIds || [],
      resultType: routeState.resultType || null,
      scope: routeState.scope || null,
      selectedInspectors: routeState.selectedInspectors || [],
      showAppendNextClosestProviders:
        (routeState.showAppendNextClosestProviders && !routeState.adequacyFilters.opportunity) || false,
      showAppendPatternOfCareProviders: routeState.showAppendPatternOfCareProviders || undefined,
      showExpandableSpecialties: routeState.showExpandableSpecialties || false,
      specialtyGroupCodes: routeState.specialtyGroupCodes || [],
      stateFilters: routeState.stateFilters || [],
      summaryKey: routeState.summaryKey || null,
      zipProjectSearchParameters: routeState.zipProjectSearchParameters || null,
    } as ExportDataRouteState;
  };

  private pluralDisplay = () => {
    switch (this.state.granularity) {
      case RegionGranularity.State:
        return "states";
      case RegionGranularity.County:
        return "counties";
      case RegionGranularity.Zip:
        return "zips";
      default:
        throw new Error(`No plural display format specified for granularity: '${this.state.granularity}'`);
    }
  };

  private singularDisplay = () => {
    switch (this.state.granularity) {
      case RegionGranularity.State:
        return "state";
      case RegionGranularity.County:
        return "county";
      case RegionGranularity.Zip:
        return "zip";
      default:
        throw new Error(`No singular display format specified for granularity: '${this.state.granularity}'`);
    }
  };

  private reportNameBase = () => {
    return `Provider Detail - ${this.scopeDisplay()} ${startCase(this.state.networkType)} Providers ${this.resultDisplay()}`;
  };

  private scopeDisplay = () => {
    if (this.state.scope === SummaryResultScope.Network) {
      return "All";
    }
    return "Servicing";
  };

  private resultDisplay = () => {
    switch (this.state.resultType) {
      case SummaryResultType.ByAddressCount:
        return `with ${this.keyDisplay()} Addresses`;
      case SummaryResultType.ByCounty:
        return `By County (${this.keyDisplay()})`;
      case SummaryResultType.ByGeocode:
        return `By Geocode (${this.keyDisplay()})`;
      case SummaryResultType.ByNPIStatus:
        return `By NPI Status (${this.keyDisplay()})`;
      case SummaryResultType.BySpecialty:
        return `By Specialty (${this.keyDisplay()})`;
      case SummaryResultType.BySpecialtyCount:
        return `with ${this.keyDisplay()} Specialties`;
      case SummaryResultType.ByState:
        return `By State  (${this.keyDisplay()})`;
      default:
        return "Overall Summary";
    }
  };

  private keyDisplay = () => {
    return this.state.displayKey;
  };

  private handleRegionSetPaneClick = () => {
    this.setState({ activeExportType: ExportType.RegionAggregate });
  };

  private handleSpecialtySummaryByCountyPaneClick = () => {
    this.setState({ activeExportType: ExportType.SpecialtySummaryByRegion });
  };

  private handleCountyAggregatePanelClick = () => {
    this.setState({ activeExportType: ExportType.CountyAggregate });
  };

  private handleStateAggregatePanelClick = () => {
    this.setState({ activeExportType: ExportType.StateAggregate });
  };

  private handleSpecialtyAggregateVolumePanelClick = () => {
    this.setState({ activeExportType: ExportType.SpecialtyAggregateVolume });
  };

  private handleSpecialtyAggregatePaneClick = () => {
    this.setState({ activeExportType: ExportType.SpecialtyAggregate });
  };

  private handleAllSpecialtiesPaneClick = () => {
    this.setState({ activeExportType: ExportType.AllSpecialties });
  };

  private handleServicingProvidersPaneClick = () => {
    this.setState({ activeExportType: ExportType.ServicingProviders });
  };

  private handleActionableProvidersPaneClick = () => {
    this.setState({ activeExportType: ExportType.ActionableProviders });
  };

  private handleAccuracyDetailsPaneClick = () => {
    this.setState({ activeExportType: ExportType.AccuracyDetail });
  };

  private handleZipSummariesPaneClick = () => {
    this.setState({ activeExportType: ExportType.ZipSummary });
  };

  private handleClosestProviderPaneClick = () => {
    this.setState({ activeExportType: ExportType.ClosestProvider });
  };

  private handleStatusReportPaneClick = () => {
    this.setState({ activeExportType: ExportType.StatusReport });
  };

  private handleZipCodeServicingProvidersPaneClick = () => {
    this.setState({ activeExportType: ExportType.ZipAnalysisAdequacyServicingProvider });
  };

  private sortedReportSettings = <T, K extends keyof T>(values: T[], key: K): T[] => {
    return sortBy(values, [(v) => new String(v[key]).toLowerCase()]);
  };
}

const WrappedExportDataProps = withFeatureFlagContext(withUserContext(ExportData));

export { WrappedExportDataProps as ExportData, ExportDataProps };
